import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Row,
  Col,
  Dropdown,
  Pagination,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTimes, FaFilter } from "react-icons/fa";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const AdminManagement = () => {
  const isMobile = window.innerWidth < 768;
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const companyKey = sessionStorage.getItem("companyKey");

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    password: "",
    confirmPassword: "",
    role: "admin",
    status: "active",
  });
  const currentUserRole = sessionStorage.getItem("role");

  const formatDate = (dateString) => {
    if (!dateString || dateString === "null" || dateString.trim() === "")
      return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    fetchAdmins();
  }, []);

  useEffect(() => {
    if (filteredAdmins.length > 0) {
      const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);
      setTotalPages(totalPages);

      if (currentPage > totalPages) {
        setCurrentPage(1);
      }
    }
  }, [filteredAdmins, itemsPerPage, currentPage]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAdmins = filteredAdmins.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    let filtered = [...admins];
    if (roleFilter !== "all") {
      filtered = filtered.filter((admin) => admin.role === roleFilter);
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((admin) => admin.status === statusFilter);
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();

      filtered = filtered.filter((admin) => {
        const validDate =
          admin.dateOfJoining &&
          admin.dateOfJoining !== "null" &&
          admin.dateOfJoining.trim() !== ""
            ? admin.dateOfJoining
            : admin.createdDate;
        const formattedDate = formatDate(validDate).toLowerCase();

        return (
          admin.id?.toString().toLowerCase().includes(searchLower) ||
          admin.fullName?.toLowerCase().includes(searchLower) ||
          admin.email?.toLowerCase().includes(searchLower) ||
          admin.mobileNumber?.toLowerCase().includes(searchLower) ||
          admin.role?.toLowerCase().includes(searchLower) ||
          admin.status?.toLowerCase().includes(searchLower) ||
          formattedDate.includes(searchLower)
        );
      });
    }

    setFilteredAdmins(filtered);
  }, [admins, searchTerm, roleFilter, statusFilter]);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList"
      );
      const filteredAdmins = response.data.filter(
        (a) => a.companyKey === companyKey
      );
      setAdmins(filteredAdmins.reverse());
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const handleEdit = (admin) => {
    setCurrentAdmin(admin);
    setFormData({
      fullName: admin.fullName,
      email: admin.email,
      mobileNumber: admin.mobileNumber,
      password: admin.password,
      confirmPassword: admin.confirmPassword,
      role: admin.role,
      status: admin.status,
    });
    setShowModal(true);
  };

  const handleDelete = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const updatedAdmins = admins.filter(
        (admin) => admin.id !== adminToDelete.id
      );
      setAdmins(updatedAdmins);
      setShowDeleteModal(false);
      setAdminToDelete(null);

      await axios.delete(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList/${adminToDelete.id}`
      );
      doAuditLog("Delete", "Admin Managemnent", adminToDelete, "");
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Admin Deleted",
        message: "Admin Deleted Successfully",
        duration: 2500, // auto-close in 2.5s
      });
      fetchAdmins();
    } catch (err) {
      setError(err.message);
      fetchAdmins();
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setCurrentAdmin(null);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const oldData = { ...currentAdmin };

      const updatedAdmins = admins.map((admin) =>
        admin.id === currentAdmin.id ? { ...admin, ...formData } : admin
      );
      setAdmins(updatedAdmins);

      setShowModal(false);

      await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList/${currentAdmin.id}`,
        formData
      );
      doAuditLog("Update", "Admin Management", currentAdmin, {
        ...currentAdmin,
        ...formData,
      });
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Admin Updated",
        message: "Admin Updated Successfully",
        duration: 2500, // auto-close in 2.5s
      });
      fetchAdmins();
    } catch (err) {
      setError(err.message);
    }
  };

  const clearFilters = () => {
    setRoleFilter("all");
    setStatusFilter("all");
    setSearchTerm("");
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const handleDownloadExcel = () => {
    if (filteredAdmins.length === 0) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "No Data",
        message: "No data available to export",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    const worksheetData = filteredAdmins.map((admin, index) => ({
      "S.I": index + 1,
      "Full Name": admin.fullName || "N/A",
      Email: admin.email || "N/A",
      Mobile: admin.mobileNumber || "N/A",
      Role: admin.role || "N/A",
      Status: admin.status || "N/A",
      "Date of Joining": formatDate(admin.dateOfJoining || admin.createdDate),
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Admin Management");

    // Auto column width
    worksheet["!cols"] = Object.keys(worksheetData[0]).map(() => ({ wch: 20 }));

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const fileName = `Admin_Management_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    saveAs(blob, fileName);
  };

  // 📄 Export PDF
  const handleDownloadPDF = () => {
    if (filteredAdmins.length === 0) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "No Data",
        message: "No Data Available to export",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    const doc = new jsPDF();
    const tableColumn = [
      "S.I",
      "Full Name",
      "Email",
      "Mobile",
      "Role",
      "Status",
      "Date of Joining",
    ];

    const tableRows = filteredAdmins.map((admin, index) => [
      index + 1,
      admin.fullName || "N/A",
      admin.email || "N/A",
      admin.mobileNumber || "N/A",
      admin.role || "N/A",
      admin.status || "N/A",
      formatDate(admin.dateOfJoining || admin.createdDate),
    ]);

    doc.setFontSize(16);
    doc.text("Admin Management Report", 14, 16);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 22,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [33, 150, 243], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });

    const fileName = `Admin_Management_${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    doc.save(fileName);
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 border rounded bg-light py-2 px-3">
        {/* Title */}
        <h5 className="mb-0 fw-bold text-dark flex-grow-1 text-start">
          Admin Management
        </h5>

        {/* Buttons */}
        <div className="d-flex align-items-center gap-2 flex-shrink-0 mt-2 mt-sm-0">
          {currentUserRole === "superadmin" && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate("/AddUser")}
              title="Export Excel"
              className="d-flex align-items-center gap-1"
            >
              Add User
            </Button>
          )}

          <Button
            variant="outline-success"
            size="sm"
            onClick={handleDownloadExcel}
            title="Export Excel"
            className="d-flex align-items-center gap-1"
          >
            Export Excel
          </Button>

          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleDownloadPDF}
            title="Export PDF"
            className="d-flex align-items-center gap-1"
          >
            Export PDF
          </Button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-2">
        {/* Search input */}
        <div className="col-12 col-md-4 mb-2 mb-md-0">
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search here..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={clearSearch}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        {/* Filter dropdowns */}
        <div className="col-12 col-md-8">
          <div className="d-flex flex-wrap justify-content-center justify-content-md-end gap-2">
            <Dropdown>
              <Dropdown.Toggle
                size="sm"
                variant="outline-secondary"
                id="role-filter"
              >
                <FaFilter className="me-2" />
                {roleFilter === "all"
                  ? "All Roles"
                  : roleFilter === "admin"
                  ? "Admins"
                  : "Super Admins"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setRoleFilter("all")}>
                  All Roles
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setRoleFilter("admin")}>
                  Admins
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setRoleFilter("manager")}>
                  Manager
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setRoleFilter("superadmin")}>
                  Super Admins
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown>
              <Dropdown.Toggle
                size="sm"
                variant="outline-secondary"
                id="status-filter"
              >
                <FaFilter className="me-2" />
                {statusFilter === "all"
                  ? "All Status"
                  : statusFilter === "active"
                  ? "Active"
                  : "Inactive"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setStatusFilter("all")}>
                  All Status
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("active")}>
                  Active
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("inactive")}>
                  Inactive
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <button
              className="btn btn-outline-danger btn-sm"
              onClick={clearFilters}
              disabled={
                roleFilter === "all" && statusFilter === "all" && !searchTerm
              }
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Filter Results Info */}
      {/* {(searchTerm || roleFilter !== "all" || statusFilter !== "all") && (
  <div className="mb-3">
    <small className="text-muted">
      Showing {filteredAdmins.length} result{filteredAdmins.length !== 1 ? 's' : ''}
      {searchTerm ? ` matching "${searchTerm}"` : ''}
      {roleFilter !== "all" ? ` with role "${roleFilter}"` : ''}
      {statusFilter !== "all" ? ` and status "${statusFilter}"` : ''}
    </small>
  </div>
)} */}


      {/* Tabs for Admin Types */}
      <div className="mb-3">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${roleFilter === "all" ? "active" : ""}`}
              onClick={() => setRoleFilter("all")}
            >
              All Admins
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${
                roleFilter === "superadmin" ? "active" : ""
              }`}
              onClick={() => setRoleFilter("superadmin")}
            >
              Super Admins
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${roleFilter === "manager" ? "active" : ""}`}
              onClick={() => setRoleFilter("manager")}
            >
              Manager
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${roleFilter === "admin" ? "active" : ""}`}
              onClick={() => setRoleFilter("admin")}
            >
              Admins
            </button>
          </li>
        </ul>
      </div>

      {/* Hide table on mobile */}
      <div className="d-none d-md-block">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th id="text-white-important">S.I</th>
              <th id="text-white-important">Full Name</th>
              <th id="text-white-important">Email</th>
              <th id="text-white-important">Mobile</th>
              <th id="text-white-important">Role</th>
              <th id="text-white-important">Status</th>
              <th id="text-white-important">Date of Joining</th>
              {currentUserRole === "superadmin" && (
                <th id="text-white-important">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentAdmins.length > 0 ? (
              currentAdmins.map((admin, index) => (
                <tr key={index}>
                  <td>{filteredAdmins.length - (indexOfFirstItem + index)}</td>
                  <td>{admin.fullName}</td>
                  <td>{admin.email}</td>
                  <td>{admin.mobileNumber}</td>
                  <td>{admin.role}</td>
                  <td>{admin.status}</td>
                  <td>
                    {admin.dateOfJoining &&
                    admin.dateOfJoining !== "null" &&
                    admin.dateOfJoining.trim() !== ""
                      ? formatDate(admin.dateOfJoining)
                      : formatDate(admin.createdDate)}
                  </td>
                  {currentUserRole === "superadmin" && (
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(admin)}
                        disabled={currentUserRole !== "superadmin"}
                        className="m-1"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(admin)}
                        disabled={currentUserRole !== "superadmin"}
                      >
                        Delete
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  No admins found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="d-block d-md-none">
        {currentAdmins.length > 0 ? (
          currentAdmins.map((admin, index) => (
            <div key={index} className="card mb-3 shadow-sm border">
              <div className="card-body p-2">
                <Table bordered size="sm" className="mb-2">
                  <tbody>
                    <tr>
                      <td className="fw-bold" style={{ width: "45%" }}>
                        SI
                      </td>
                      <td>
                        {filteredAdmins.length - (indexOfFirstItem + index)}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Full Name</td>
                      <td>{admin.fullName}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Email</td>
                      <td>{admin.email}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Mobile</td>
                      <td>{admin.mobileNumber}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Role</td>
                      <td>{admin.role}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Status</td>
                      <td>{admin.status}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Date of Joining</td>
                      <td>
                        {admin.dateOfJoining &&
                        admin.dateOfJoining !== "null" &&
                        admin.dateOfJoining.trim() !== ""
                          ? formatDate(admin.dateOfJoining)
                          : formatDate(admin.createdDate)}
                      </td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="flex-fill me-1"
                            onClick={() => handleEdit(admin)}
                            disabled={currentUserRole !== "superadmin"}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="flex-fill ms-1"
                            onClick={() => handleDelete(admin)}
                            disabled={currentUserRole !== "superadmin"}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-muted py-3">No admins found</div>
        )}
      </div>

      <div className="col-12 text-end">
        <span className="text-muted small">
          Showing {indexOfFirstItem + 1} to{" "}
          {Math.min(indexOfLastItem, filteredAdmins.length)} of{" "}
          {filteredAdmins.length} admins
        </span>
      </div>

            {/* Pagination Section */}
      {filteredAdmins.length > 0 && (
        <div className="row align-items-center justify-content-between mb-2  p-2">
          {/* Items per page selector */}
          <div className="col-5 col-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="items-per-page-dropdown"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {/* Mobile: Show simplified pagination */}
              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                /* Desktop: Show full pagination */
                Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  )
                )
              )}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <Modal show={showModal} onHide={handleClose} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Edit Admin</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>

            {/* <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              {currentUserRole === "superadmin" && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={10} /> : <EyeIcon size={10} />}
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>
              )}
            </Row>

            {currentUserRole === "superadmin" && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={10} /> : <EyeIcon size={10} />}
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            )} */}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    disabled={
                      currentUserRole !== "superadmin" &&
                      currentAdmin?.role === "superadmin"
                    }
                  >
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="superadmin">Super Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Warning!</strong> This action cannot be undone.
          </Alert>
          <p>Are you sure you want to delete the admin account for:</p>
          <strong>
            {adminToDelete?.fullName} ({adminToDelete?.email})
          </strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete Admin
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminManagement;
